import { uiAccentContrast, uiAccentSolid } from '~/utils/ui'
import type { MorningTheme } from '~~/shared/types/morning'

const choices: MorningTheme[] = ['auto', 'light', 'dark']

export function useAppearance(
  configuredTheme: MaybeRefOrGetter<MorningTheme>,
  accent: MaybeRefOrGetter<string>,
) {
  const analytics = useAnalytics()
  const selectedTheme = ref<MorningTheme>('auto')
  const initialized = ref(false)

  useHead(() => ({
    htmlAttrs: {
      'data-theme': selectedTheme.value,
      style: `--accent-solid: ${uiAccentSolid(toValue(accent))}`,
    },
  }))

  function applyTheme() {
    if (!import.meta.client) return
    const value = toValue(accent)
    document.documentElement.style.setProperty('--accent-config', value)
    document.documentElement.style.setProperty('--accent-solid', uiAccentSolid(value))
    document.documentElement.style.setProperty('--accent-light-contrast', uiAccentContrast(value))
    document.documentElement.style.setProperty(
      '--accent-dark-contrast',
      uiAccentContrast(value, true),
    )
  }

  function cycleTheme() {
    const currentIndex = choices.indexOf(selectedTheme.value)
    selectedTheme.value = choices[(currentIndex + 1) % choices.length] ?? 'auto'
  }

  onMounted(() => {
    const saved = localStorage.getItem('morning-theme')
    selectedTheme.value = choices.includes(saved as MorningTheme)
      ? (saved as MorningTheme)
      : toValue(configuredTheme)
    initialized.value = true
    applyTheme()
  })

  watch(selectedTheme, (theme) => {
    if (!initialized.value || !import.meta.client) return
    localStorage.setItem('morning-theme', theme)
    applyTheme()
    analytics.trackEvent('preference_action', {
      domain: 'appearance',
      action: 'theme_change',
      value: theme,
    })
  })

  watch(() => toValue(accent), applyTheme)

  return { selectedTheme, cycleTheme }
}
